export interface NicuAlertItem {
  jam: number;
  param: string;
  level: "critical" | "warning";
  value: number;
  message: string;
}

export interface NicuObsDetail {
  respirasi?: { retraksi?: string; sianosis?: string; jalan_masuk_udara?: string; grunting?: string };
  warna?: string;
  tonus?: string;
  aktivitas?: string;
  kesadaran?: string;
  posisi?: string;
  hisap_lendir?: string;
  udema?: { ekstremitas?: string; palpebra?: string; genital?: string };
  perut?: { distensi?: string; konsistensi?: string; tali_pusat_merah?: string; bising_usus?: string };
  ventilasi?: { mode?: string; rr?: string; fio2?: string; pip_peep?: string; ti_ie?: string; flow?: string; cek_et?: string };
  intermina?: { a?: string; b?: string; c?: string; d?: string; e?: string };
  feeding?: { asi_cara?: string; sf_cara?: string };
  keluar?: { residu?: string; iwl?: string; diuresis?: string };
  lab?: {
    ph?: string; pco2?: string; po2?: string; hco3?: string; be?: string;
    // `ar` = Angka Eritrosit (label blanko: AE) — key lama dipertahankan agar record lama tetap terbaca
    hb?: string; al?: string; ar?: string; at?: string; hct?: string; it_ratio?: string;
    na?: string; k?: string; cl?: string; ca?: string;
    gds?: string; album?: string; bill_tot?: string; bill_indirek?: string; crp?: string;
  };
}

export interface MonitoringHarianNicuObservasiInterface {
  id: number;
  monitoring_harian_nicu_id: number;
  jam: number;
  suhu: number | null;
  kelembapan_indikator: string | null;
  hr: number | null;
  td: string | null;
  perabaan_nadi: string | null;
  spo2: number | null;
  rr: number | null;
  tindakan: string | null;
  data_masalah: string | null;
  instruksi_dokter: string | null;
  verifikasi: string | null;
  obat: string | null;
  cairan_iv: string | null;
  founding_asi: string | null;
  founding_sf: string | null;
  cairan_keluar_bak: string | null;
  muntah: string | null;
  bab: string | null;
  laboratorium: string | null;
  detail: NicuObsDetail | null;
  perawat_user_id: number;
  perawat?: any;
  alert_level: Array<{ param: string; level: string; value: number; message: string }>;
  down_score: number | null;
  created_at: string;
  updated_at: string;
}

export interface NicuAsesmen {
  a?: string;
  b?: string;
  c?: string;
  d?: string;
  e?: string;
  ast_sp?: string;
}

export interface NicuShiftTotal {
  key: string;
  label: string;
  jam_count: number;
  intake: number;
  output: number;
  balans: number;
  urin: number;
  diuresis: number | null;
}

export interface NicuRekap24 {
  intravena: number;
  feeding: number;
  residu: number;
  muntah: number;
  urin_bab: number;
  iwl: number;
  intake: number;
  output: number;
  balans: number;
  diuresis: number | null;
}

export interface NicuShiftTotals {
  shifts: NicuShiftTotal[];
  rekap24: NicuRekap24;
}

export interface MonitoringHarianNicuInterface {
  id: number;
  pasien_rm: string;
  register_id: number;
  unit_id: string;
  tanggal: string;
  hari_rawat_ke: number | null;
  umur_hari: number | null;
  umur_kehamilan: number | null;
  bb_lahir: number | null;
  bb_masuk: number | null;
  bb_sekarang: number | null;
  masalah_aktif: string | null;
  masalah_ibu: string | null;
  ibu_dirawat_di: string | null;
  paritas: string | null;
  tgl_lahir: string | null;
  tgl_masuk: string | null;
  asesmen: NicuAsesmen | null;
  et_no: string | null;
  et_kedalaman: string | null;
  catatan_penting: string | null;
  rekap_cairan: any | null;
  dpjp_user_id: number | null;
  dpjp_signed_at: string | null;
  perawat_user_id: number | null;
  signed_at: string | null;
  creator_user_id: number;
  observasi: MonitoringHarianNicuObservasiInterface[];
  alerts: NicuAlertItem[];
  shift_totals?: NicuShiftTotals;
  pasien?: any;
  dpjp?: any;
  perawat?: any;
  created_at: string;
  updated_at: string;
}

export interface MonitoringHarianNicuListItem {
  id: number;
  tanggal: string;
  bb_sekarang: number | null;
  hari_rawat_ke: number | null;
  observasi_count: number;
  is_signed: boolean;
  is_dpjp_verified: boolean;
  alert_count: number;
  perawat: string | null;
  dpjp: string | null;
}

export const NICU_OBS_FIELDS: Array<{ key: string; label: string; type: "number" | "text" }> = [
  { key: "suhu", label: "Suhu", type: "number" },
  { key: "kelembapan_indikator", label: "Suhu/Kelembapan Inkubator", type: "text" },
  { key: "hr", label: "HR", type: "number" },
  { key: "td", label: "TD", type: "text" },
  { key: "spo2", label: "SpO2", type: "number" },
  { key: "rr", label: "RR (Frekuensi Nafas)", type: "number" }
];

export interface NicuOption {
  value: string;
  label: string;
}

// Item v-select: "kode — label" agar kode yang tersimpan tetap pendek.
export function optItems(opts: NicuOption[]): Array<{ value: string; text: string }> {
  return opts.map(o => ({ value: o.value, text: `${o.value} — ${o.label}` }));
}

// Baris keterangan kode: "4 Bounding · 3 Keras · ...".
export function optLegend(opts: NicuOption[]): string {
  return opts.map(o => `${o.value} ${o.label}`).join(" · ");
}

export const PERABAAN_NADI_OPTIONS: NicuOption[] = [
  { value: "4", label: "Bounding" },
  { value: "3", label: "Keras" },
  { value: "2", label: "Lemah" },
  { value: "1", label: "Halus" },
  { value: "0", label: "Tak teraba" }
];

// Komponen skor Downe
export const RESPIRASI_SCORE_OPTIONS: { [key: string]: NicuOption[] } = {
  frekuensi_nafas: [
    { value: "0", label: "< 60x/menit" },
    { value: "1", label: "60-80x/menit" },
    { value: "2", label: "> 80x/menit" }
  ],
  retraksi: [
    { value: "0", label: "Tidak ada retraksi" },
    { value: "1", label: "Retraksi ringan" },
    { value: "2", label: "Retraksi parah" }
  ],
  sianosis: [
    { value: "0", label: "Tidak sianosis" },
    { value: "1", label: "Sianosis hilang dengan O2" },
    { value: "2", label: "Sianosis meski diberi O2" }
  ],
  jalan_masuk_udara: [
    { value: "0", label: "Udara masuk bilateral baik" },
    { value: "1", label: "Penurunan ringan udara masuk" },
    { value: "2", label: "Tidak ada udara masuk" }
  ],
  grunting: [
    { value: "0", label: "Tidak ada grunting" },
    { value: "1", label: "Dpt didengar dgn stetoskop" },
    { value: "2", label: "Dpt didengar tanpa alat bantu" }
  ]
};

export const WARNA_KULIT_OPTIONS: NicuOption[] = [
  { value: "K1", label: "Kuning (Kramer 1)" },
  { value: "K2", label: "Kuning (Kramer 2)" },
  { value: "K3", label: "Kuning (Kramer 3)" },
  { value: "K4", label: "Kuning (Kramer 4)" },
  { value: "A", label: "Anemis" },
  { value: "P", label: "Plethore" },
  { value: "N", label: "Normal" },
  { value: "B", label: "Biru" },
  { value: "M", label: "Mottled" }
];

export const TONUS_OPTIONS: NicuOption[] = [
  { value: "1", label: "Normal" },
  { value: "2", label: "Hipotoni" },
  { value: "3", label: "Hipertoni" },
  { value: "4", label: "Flacid" },
  { value: "5", label: "Paralise" }
];

export const AKTIVITAS_OPTIONS: NicuOption[] = [
  { value: "T", label: "Tidur" },
  { value: "A", label: "Aktif" },
  { value: "I", label: "Iritabel" },
  { value: "M", label: "Menangis" },
  { value: "L", label: "Lethargia" },
  { value: "J", label: "Jitternes" },
  { value: "S", label: "Dalam sedasi" },
  { value: "Tn", label: "Tenang" }
];

export const KESADARAN_OPTIONS: NicuOption[] = [
  { value: "S1", label: "Mata tertutup, tidak menangis, tidak bergerak" },
  { value: "S2", label: "Mata tertutup, tidak menangis, bergerak" },
  { value: "S3", label: "Mata terbuka, tidak menangis, tidak bergerak" },
  { value: "S4", label: "Mata terbuka, tidak menangis, bergerak" },
  { value: "S5", label: "Mata terbuka, menangis, bergerak" }
];

export const POSISI_OPTIONS: NicuOption[] = [
  { value: "Miki", label: "Miring kiri" },
  { value: "Mika", label: "Miring kanan" },
  { value: "TLT", label: "Terlentang" },
  { value: "TKP", label: "Tengkurap" }
];

export const HISAP_LENDIR_OPTIONS: NicuOption[] = [
  { value: "M", label: "Mulut" },
  { value: "H", label: "Hidung" },
  { value: "E", label: "ET" },
  { value: "B", label: "BW" }
];

export const BAB_OPTIONS: NicuOption[] = [
  { value: "S", label: "Lunak" },
  { value: "F", label: "Berbentuk" },
  { value: "W", label: "Cair" },
  { value: "MEC", label: "Mekonium" },
  { value: "MU", label: "Lendir" },
  { value: "B", label: "Coklat" },
  { value: "Y", label: "Kuning" },
  { value: "G", label: "Hijau" },
  { value: "FB", label: "Darah" },
  { value: "BL", label: "Hitam" },
  { value: "P", label: "Pucat" },
  { value: "Sd", label: "Butir-butir" }
];

export const WARNA_CAIRAN_OPTIONS: NicuOption[] = [
  { value: "M", label: "Merah" },
  { value: "H", label: "Hijau" },
  { value: "Ht", label: "Hitam" },
  { value: "S", label: "Susu" },
  { value: "C", label: "Coklat/keruh" },
  { value: "J", label: "Jernih" },
  { value: "K", label: "Kental" }
];

export const CARA_FEEDING_OPTIONS: NicuOption[] = [
  { value: "Tts", label: "Tetes" },
  { value: "S", label: "Sonde" },
  { value: "Sd", label: "Sendok" },
  { value: "Gl", label: "Gelas" },
  { value: "Nt", label: "Netek" }
];

export const MODE_VENTILASI_OPTIONS: NicuOption[] = [
  { value: "NK", label: "Nasal kanul" },
  { value: "HB", label: "Head box" },
  { value: "Nt", label: "Nasal kateter" },
  { value: "OI", label: "O2 inkubator" },
  { value: "NCPAP", label: "Nasal CPAP" },
  { value: "IMV", label: "Ventilator Mode IMV" }
];

export function downRrScore(rr: number | string | null | undefined): number | null {
  if (rr === null || rr === undefined || rr === "") return null;
  const v = Number(rr);
  if (Number.isNaN(v)) return null;
  return v < 60 ? 0 : v <= 80 ? 1 : 2;
}

export function downScore(
  rr: number | string | null | undefined,
  respirasi: { [key: string]: string | undefined } | null | undefined
): number | null {
  let sum = 0;
  let any = false;
  const rrS = downRrScore(rr);
  if (rrS !== null) {
    sum += rrS;
    any = true;
  }
  for (const key of ["retraksi", "sianosis", "jalan_masuk_udara", "grunting"]) {
    const v = respirasi ? respirasi[key] : null;
    if (v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))) {
      sum += Number(v);
      any = true;
    }
  }
  return any ? sum : null;
}
